import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import type { Device } from '@/types/api';
import { Badge } from '@/components/ui/badge';
import { SignalIcon } from '@/components/ui/signal-icon';
import { cn, formatRelativeTime } from '@/lib/utils';
import { formatUnitOfMeasureSuffix } from '@/features/devices/utils/salt-config';
import { deviceDetailPath } from '@/features/devices/utils/device-navigation';
import { hasFreshTransmission } from '@/features/devices/utils/has-fresh-transmission';
import { deviceStatus } from '../utils/device-status';

interface PropertyDeviceCardProps {
  device: Device;
}

// Card-style row for the regular property device grid. Mirrors the Setup
// Mode card aesthetically (rounded, lifted on hover, focus-visible ring)
// but carries the richer detail the regular view has access to: current
// reading, signal strength, active alarms, and last-seen time.
//
// Visual hierarchy:
//   - Header row: location (Building – Unit) + serial mono + signal icon
//     and last-seen time tucked top-right.
//   - Body row: current reading prominent, alarm chips on the right.
//   - Cards with active alarms get a thick red left accent so the eye
//     spots them at a glance in a wall of healthy meters.
export function PropertyDeviceCard({ device }: PropertyDeviceCardProps) {
  const navigate = useNavigate();

  // Prefer the computed value + computed unit (SALT multiplier already
  // applied) over the raw decoder output. Falls back to raw for older
  // payloads. The unit follows the configured UoM so a calibrated SALT
  // meter shows "gal." instead of "pulses".
  const tx = device.latestTransmission;
  const reading = tx ? (tx.computedWaterTotalization ?? tx.waterTotalization) : undefined;
  const readingUnit = formatUnitOfMeasureSuffix(tx?.computedUnitOfMeasure ?? tx?.unitOfMeasure);

  // When the latest transmission predates this device's current unit
  // assignment (or the device has never transmitted at all), the signal
  // icon, last-seen time, and reading are all carried over from a prior
  // assignment and would mislead the user — green dot, fresh-looking
  // numbers — into thinking the meter is live. Suppress those values and
  // show the awaiting-uplink visual instead, matching the Setup Mode card.
  const isFresh = hasFreshTransmission(device);
  // 'waiting' = no join AND no post-assignment uplink (the device hasn't
  // even handshaked the network yet). 'recognized' = at least one of the
  // two has happened. The card surfaces "Send Join Request" copy in the
  // 'waiting' state so an installer who exits Setup Mode without joining
  // sees the same prompt they'd see inside Setup Mode — consistent
  // mental model across the two views.
  const status = deviceStatus(device);
  const isWaitingForJoin = status === 'waiting' && device.unitAssignedAt != null;

  const alerts = device.activeAlerts ?? [];
  const hasAlarms = alerts.length > 0;
  const primaryAlert = hasAlarms ? alerts[0] : null;

  // Two-line identity: the unit (or building when unassigned) is the primary
  // line so it stays readable even when the building name is long. Long
  // building names wrap onto a second line below at a smaller weight, which
  // keeps every card's unit visible at a glance.
  const primaryLine = device.unitNumber
    ? `Unit ${device.unitNumber}`
    : device.buildingName ?? null;
  const secondaryLine = device.unitNumber ? device.buildingName ?? null : null;
  const accessibleLocation = [secondaryLine, primaryLine].filter(Boolean).join(' – ') || 'Unassigned';

  return (
    <button
      type="button"
      onClick={() => navigate(deviceDetailPath(
        device.id,
        device.propertyId ? { kind: 'property', id: device.propertyId } : undefined,
      ))}
      className={cn(
        'group relative flex flex-col gap-3 rounded-xl border bg-white dark:bg-gray-800 p-4 text-left transition-all',
        'border-gray-200 dark:border-gray-700',
        'hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md hover:-translate-y-0.5',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:border-primary-500',
        // Alarmed cards get a thick red accent stripe down the left edge.
        // Subtle enough that healthy cards still feel quiet, loud enough
        // to draw the eye in a 100-card grid.
        hasAlarms && 'border-l-[3px] border-l-error-500 dark:border-l-error-500',
      )}
      aria-label={
        `${accessibleLocation}, serial ${device.serialNumber}` +
        (hasAlarms ? `, ${alerts.length} active alarm${alerts.length === 1 ? '' : 's'}` : '')
      }
    >
      {/* Header — identity on the left, signal + time on the right. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight">
              {primaryLine ?? <span className="italic text-gray-500 dark:text-gray-400">Unassigned</span>}
            </p>
            {/* Device-type cue. Short form on the card to keep the unit
                label readable on narrow widths — a long "SALT Transmitter"
                badge ate into the unit number truncation. Full label
                still lives on the Devices table where there's more room. */}
            {device.deviceType === 'SALTTransmitter' && (
              <Badge variant="info" size="sm" className="flex-shrink-0">
                SALT
              </Badge>
            )}
            {device.deviceType === 'WaterMeter' && (
              <Badge variant="secondary" size="sm" className="flex-shrink-0">
                Water Meter
              </Badge>
            )}
          </div>
          {secondaryLine && (
            <p
              className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2 leading-snug"
              title={secondaryLine}
            >
              {secondaryLine}
            </p>
          )}
          <p className="mt-0.5 text-xs font-mono text-gray-500 dark:text-gray-400 truncate">
            {device.serialNumber}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isFresh ? (
            <>
              <SignalIcon
                rssi={device.latestTransmission?.rssi}
                meta={{
                  snr: device.latestTransmission?.snr,
                  spreadingFactor: device.latestTransmission?.spreadingFactor,
                  gatewayEui: device.latestTransmission?.gatewayEui,
                  timestamp: device.latestTransmission?.timestamp ?? device.lastSeenAt,
                }}
              />
              <p className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">
                {formatRelativeTime(device.lastSeenAt!)}
              </p>
            </>
          ) : isWaitingForJoin ? (
            // Not joined yet — pulsing red dot to draw attention. The
            // body's empty state below carries the "Send Join Request"
            // copy. Keeps the right column compact.
            <span aria-hidden="true" className="relative flex-shrink-0 w-3 h-3 rounded-full bg-error-500">
              <span className="absolute inset-0 rounded-full bg-error-500/60 animate-ping" />
            </span>
          ) : (
            // Joined but no fresh uplink yet — solid green dot, no pulse.
            // Mirrors the Setup Mode tile's "Joined" state: the device
            // has handshaked the network successfully, just hasn't sent
            // a sensor reading at this unit yet. Red would suggest the
            // join is still pending, which it isn't.
            <span
              aria-hidden="true"
              className="flex-shrink-0 w-3 h-3 rounded-full bg-success-500 shadow-[0_0_8px_rgba(34,226,94,0.45)]"
            />
          )}
        </div>
      </div>

      {/* Body — reading on the left, alarm chips on the right. mt-auto so
          the row glues to the bottom of the card even when content above
          is short, keeping the grid's vertical rhythm consistent.
          When the device hasn't transmitted at this unit yet, the reading
          is suppressed entirely (it would belong to a prior assignment). */}
      <div className="flex items-end justify-between gap-3 mt-auto">
        <div className="min-w-0">
          {isFresh && reading != null ? (
            <>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 tabular-nums leading-none">
                {reading.toLocaleString()}
                <span className="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400">
                  {readingUnit}
                </span>
              </p>
              {/* Raw decoder value — what the meter is internally counting,
                  no multiplier / offset / baseline applied. Useful as the
                  "what does the dial say" sanity check at a glance. */}
              {tx?.waterTotalization != null && (
                <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500 tabular-nums leading-none">
                  Raw: {tx.waterTotalization.toLocaleString()}
                </p>
              )}
            </>
          ) : (
            <p
              className={cn(
                'text-xs italic',
                isWaitingForJoin
                  ? 'text-error-600 dark:text-error-400 font-medium not-italic'
                  : 'text-gray-400 dark:text-gray-500',
              )}
            >
              {isFresh
                ? 'No reading yet'
                : isWaitingForJoin
                  ? 'Send Join Request'
                  : 'Awaiting first uplink at this unit'}
            </p>
          )}
        </div>
        {hasAlarms && primaryAlert && (
          <div className="flex items-center gap-1 flex-shrink-0 max-w-[55%]">
            <Badge variant="error" size="sm" className="truncate">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
              <span className="truncate">{primaryAlert.displayLabel}</span>
            </Badge>
            {alerts.length > 1 && (
              <Badge variant="error" size="sm">
                +{alerts.length - 1}
              </Badge>
            )}
          </div>
        )}
      </div>
    </button>
  );
}
